import { FileText, UploadCloud, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Seçilen dosya; `file` yeni seçimde dolar (düzenlemede mevcut dosya yalnız ad/boyutla gösterilir). */
export type PickedFile = { name: string; size: number; file?: File }

type Props = { id: string; value: PickedFile | null; onChange: (f: PickedFile | null) => void; accept: string; hint: string; invalid?: boolean; maxBytes?: number }

const fmtSize = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(b / 1024))} KB`)

/** Sürükle-bırak ya da seç; dosya sunucuya yüklenmek üzere forma aktarılır. */
export function Dropzone({ id, value, onChange, accept, hint, invalid, maxBytes = 10 * 1024 * 1024 }: Props) {
  const { t } = useTranslation()
  const ref = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [error, setError] = useState('')
  const take = (f: File | undefined) => {
    if (!f) return
    if (f.size > maxBytes) {
      setError(t('errors.FILE_TOO_LARGE'))
      return
    }
    setError('')
    onChange({ name: f.name, size: f.size, file: f })
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-lg border p-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
          <FileText className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{value.name}</p>
          <p className="text-xs text-muted-foreground">{fmtSize(value.size)}</p>
        </div>
        <Button type="button" size="icon" variant="ghost" className="size-8" aria-label={t('crm.dropzone.remove')} onClick={() => onChange(null)}>
          <X />
        </Button>
      </div>
    )
  }
  return (
    <div>
      <button
        id={id}
        type="button"
        onClick={() => ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setOver(false)
          take(e.dataTransfer.files[0])
        }}
        aria-invalid={invalid}
        className={cn(
          'flex w-full flex-col items-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          over ? 'border-primary bg-primary/[0.06]' : invalid ? 'border-destructive/50' : 'hover:border-primary/40 hover:bg-muted/40',
        )}
      >
        <UploadCloud className="size-7 text-primary" aria-hidden />
        <span>
          {t('crm.dropzone.drop')} <span className="font-medium text-primary underline">{t('crm.dropzone.pick')}</span>
        </span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </button>
      <input ref={ref} type="file" accept={accept} className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => take(e.target.files?.[0])} />
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
